import React, { useState, useEffect } from 'react';
import { Button, Tooltip, Box, Dialog, DialogTitle, DialogContent, DialogActions, Typography, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { GetApp as InstallIcon, Share as ShareIcon, AddCircle as AddCircleIcon, ArrowForward as ArrowForwardIcon } from '@mui/icons-material';

const PWAInstallButton = ({ variant = 'outlined', size = 'small', fullWidth = false }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isAvailable, setIsAvailable] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // Detect iOS
    const checkIOS = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
      const isIOSStandalone = window.navigator.standalone === true;
      const isIOSInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches && isIOSDevice;
      
      if (isIOSDevice || isIOSStandalone || isIOSInStandaloneMode) {
        setIsIOS(true);
        return true;
      }
      return false;
    };

    // Check if already installed
    const checkInstalled = () => {
      // Check for standalone display mode (PWA installed on Android/Desktop)
      if (window.matchMedia('(display-mode: standalone)').matches) {
        return true;
      }
      
      // Check for iOS standalone mode
      if (window.navigator.standalone === true) {
        return true;
      }
      
      // Check for Android app referrer
      if (document.referrer.includes('android-app://')) {
        return true;
      }
      
      return false;
    };

    // Check iOS first
    const iosDevice = checkIOS();

    // Check immediately on mount
    if (checkInstalled()) {
      setIsInstalled(true);
      return;
    }

    // For iOS, show install button (they need manual instructions)
    if (iosDevice) {
      setIsAvailable(true);
      return;
    }

    // For Android/Desktop: Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsAvailable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      setIsAvailable(false);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    // iOS: Show instructions dialog
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    // Android/Desktop: Use native install prompt
    if (!deferredPrompt) {
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }

    // Clear the deferredPrompt
    setDeferredPrompt(null);
    setIsAvailable(false);
  };

  // Don't show if already installed or not available
  if (isInstalled || !isAvailable) {
    return null;
  }

  return (
    <>
      <Tooltip title={isIOS ? "Tap to see installation instructions" : "Install PTShub SMS app for better experience"}>
        <Button
          variant={variant}
          size={size}
          fullWidth={fullWidth}
          startIcon={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <InstallIcon />
            </Box>
          }
          onClick={handleInstall}
          sx={{ textTransform: 'none' }}
        >
          Install App
        </Button>
      </Tooltip>

      {/* iOS Installation Instructions Dialog */}
      <Dialog
        open={showIOSInstructions}
        onClose={() => setShowIOSInstructions(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Install PTShub SMS on iOS
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Follow these steps to add PTShub SMS to your home screen:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <ShareIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Tap the Share button"
                secondary="Located at the bottom of Safari (square with arrow icon)"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <AddCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Select 'Add to Home Screen'"
                secondary="Scroll down in the share menu to find this option"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <ArrowForwardIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Tap 'Add'"
                secondary="Confirm the installation in the top right corner"
              />
            </ListItem>
          </List>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
            Once installed, you can access PTShub SMS directly from your home screen!
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setShowIOSInstructions(false)} variant="contained">
            Got it!
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PWAInstallButton;

